import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Sidebar } from '@/components/layout/Sidebar';
import { TopBar } from '@/components/layout/TopBar';
import { Skeleton } from '@/components/ui/Skeleton';

export function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  // Close the mobile drawer whenever the route changes.
  useEffect(() => setDrawerOpen(false), [location.pathname, location.search]);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[256px_1fr]">
      {/* Persistent sidebar — large screens */}
      <aside className="hidden lg:block border-r border-line bg-panel sticky top-0 h-dvh">
        <Sidebar />
      </aside>

      {/* Slide-in drawer — small screens */}
      <AnimatePresence>
        {drawerOpen ? (
          <div className="fixed inset-0 z-[var(--z-overlay)] lg:hidden">
            <motion.div
              className="absolute inset-0 bg-[rgba(10,14,18,0.5)] backdrop-blur-[2px]"
              onClick={() => setDrawerOpen(false)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduce ? 0 : 0.2 }}
            />
            <motion.aside
              className="absolute inset-y-0 left-0 w-[256px] bg-panel border-r border-line"
              initial={reduce ? { opacity: 0 } : { x: '-100%' }}
              animate={reduce ? { opacity: 1 } : { x: 0 }}
              exit={reduce ? { opacity: 0 } : { x: '-100%' }}
              transition={{ duration: reduce ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
            >
              <Sidebar />
            </motion.aside>
          </div>
        ) : null}
      </AnimatePresence>

      <div className="relative z-[2] flex min-h-dvh flex-col min-w-0">
        <TopBar onOpenSidebar={() => setDrawerOpen(true)} />
        <main className="flex-1 px-4 md:px-7 py-6 md:py-8">
          <Suspense
            fallback={
              <div className="mx-auto w-full max-w-[1320px]">
                <Skeleton className="h-[60dvh] w-full rounded-card" />
              </div>
            }
          >
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}
